import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation, useMatch, useOutletContext } from 'react-router-dom';
import { TypeMainOutlet } from '..';

import eventBus from '../../../utils/eventBus';
import timeFast from '../../../assets/production-flow/time-fast.svg';
import movement from '../../../assets/stock/TableMovementHeader/refresh-active.svg';
import pcp from '../../../assets/production-flow/pcp.svg';
import { SectionName, TabsDiv, Tab } from '../../../components/styles/subheader-style';
import { Content } from './style';
import ModuleKanban from '../Pipelines/ModuleKanban';
import { ViewModeSelector } from '../Pipelines/PipelineControls';
import { Filter as LucideFilter } from 'lucide-react';
import styled from 'styled-components';
import { usePersistedFilters } from '../../../hooks/usePersistedFilters';
import PageHeader from '../../../components/StandardPage/PageHeader';
import { Header, Filter as SubHeaderFilterType } from '../../../components/SubHeaderFilter/types';
import { useSearch } from '../../../contexts/SearchContext';

type PCPViewType = 'table' | 'board';

const PRODUCTION_FASHION_BASE = '/panel/production-fashion';

export interface ProductionFashionOutletType {
  tabIndex: number;
  setTabIndex: React.Dispatch<React.SetStateAction<number>>;
  pcpView: PCPViewType;
  setPCPView: React.Dispatch<React.SetStateAction<PCPViewType>>;
  quantity: number;
  setQuantity: React.Dispatch<React.SetStateAction<number>>;
  refresh: boolean;
  setRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  appliedFilters: SubHeaderFilterType[];
  search?: string;
}

const TAB_INDEX_ORDEM_PRODUCAO = 0;
const TAB_INDEX_MOVEMENT = 1;
const TAB_INDEX_PCP = 2;
const TAB_INDEX_BOARD = 3;

export const productionFashionOrderHeaders: Header[] = [
  { key: 'codeOfOP', label: 'OP', type: 'string' },
  { key: 'fashionOrderId', label: 'Pedido', type: 'number' },
  { key: 'product', label: 'Produto', type: 'string' },
  { key: 'color', label: 'Cor', type: 'string' },
  { key: 'totalItems', label: 'Qtd. de itens', type: 'number' },
  { key: 'cutter', label: 'Cortador', type: 'string' },
  { key: 'dressmaker', label: 'Costureira', type: 'string' },
  { key: 'status', label: 'Status', type: 'string', selectOptions: [
    { value: 'Pendente', label: 'Pendente' },
    { value: 'Iniciado', label: 'Iniciado' },
    { value: 'Concluído', label: 'Concluído' }
  ]}
];

export const productionFashionMovementHeaders: Header[] = [
  { key: 'movementDirection', label: 'Tipo', type: 'string', selectOptions: [
    { value: 'Entrada', label: 'Entrada' },
    { value: 'Saída', label: 'Saída' },
    { value: 'Ordem de compra', label: 'Ordem de compra' },
  ]},
  { key: 'origin', label: 'Origem', type: 'string' },
  { key: 'rawMaterialName', label: 'Código', type: 'string' },
  { key: 'quantity', label: 'Movimento', type: 'string' },
  { key: 'oldTotalWeight', label: 'Peso anterior (kg)', type: 'number' },
  { key: 'newTotalWeight', label: 'Peso novo (kg)', type: 'number' },
  { key: 'oldLengthInMm', label: 'Comp. anterior', type: 'number' },
  { key: 'newLengthInMm', label: 'Comp. novo', type: 'number' },
  { key: 'oldQuantity', label: 'Qtd. anterior', type: 'number' },
  { key: 'newQuantity', label: 'Qtd. nova', type: 'number' },
  { key: 'status', label: 'Status', type: 'string', selectOptions: [
    { value: '410', label: 'Desfeito' },
    { value: 'abatido', label: 'Abatido' },
    { value: 'PENDING', label: 'Pendente' },
    { value: 'APPROVED', label: 'Aprovada' },
    { value: 'ORDERED', label: 'Encomendada' },
    { value: 'DELIVERED', label: 'Comprada' },
    { value: 'COMPLETED', label: 'Concluída' },
    { value: 'CANCELLED', label: 'Cancelada' }
  ]},
  { key: 'createdAt', label: 'Data', type: 'date' }
];

const ContentStartFashion = styled(Content)`
background-color: #f4f4f4;
overflow: auto;
`;

const tabs = [
  { path: PRODUCTION_FASHION_BASE, image: timeFast, name: 'Ordem de produção', index: TAB_INDEX_ORDEM_PRODUCAO },
  { path: `${PRODUCTION_FASHION_BASE}/movement`, image: movement, name: 'Movimentação', index: TAB_INDEX_MOVEMENT },
  { path: `${PRODUCTION_FASHION_BASE}/pcp`, image: pcp, name: 'PCP', index: TAB_INDEX_PCP },
  { path: `${PRODUCTION_FASHION_BASE}/board`, image: movement, name: 'Funil', index: TAB_INDEX_BOARD }
];

const ProductionFashionContent = () => {
const { clearSearch } = useSearch();
const { search } = useOutletContext<TypeMainOutlet>();
const location = useLocation();
const navigate = useNavigate();
const startMatch = useMatch('/panel/production-fashion/start/:orderId');
const createMatch = useMatch('/panel/production-fashion/create');
const boardMatch = useMatch('/panel/production-fashion/board');
const [tabIndex, setTabIndex] = useState<number>(TAB_INDEX_ORDEM_PRODUCAO);
const [pcpView, setPCPView] = useState<PCPViewType>('table');
const [quantity, setQuantity] = useState(50);
const [refresh, setRefresh] = useState(false);

const orderFiltersState = usePersistedFilters('production_fashion_order');
const movementFiltersState = usePersistedFilters('production_fashion_movement');

const activeFiltersState = tabIndex === TAB_INDEX_MOVEMENT ? movementFiltersState : orderFiltersState;

const isFilterableTab = tabIndex === TAB_INDEX_ORDEM_PRODUCAO || tabIndex === TAB_INDEX_MOVEMENT;

useEffect(() => {
  const bumpMovements = () => setRefresh((r) => !r);
  eventBus.on('refreshProductionTable', bumpMovements);
  return () => {
    eventBus.off('refreshProductionTable', bumpMovements);
  };
}, []);

const handleAddOPClick = useCallback(() => {
  navigate('/panel/production-fashion/create');
}, [navigate]);

useEffect(() => {
  const p = location.pathname;
  if (p.includes('/production-fashion/start/')) {
    return;
  }
  let next = TAB_INDEX_ORDEM_PRODUCAO;
  if (p.includes('/production-fashion/board')) {
    next = TAB_INDEX_BOARD;
  } else if (p.includes('/production-fashion/pcp')) {
    next = TAB_INDEX_PCP;
  } else if (p.includes('/production-fashion/movement')) {
    next = TAB_INDEX_MOVEMENT;
  } else if (/\/production-fashion\/?$/.test(p)) {
    next = TAB_INDEX_ORDEM_PRODUCAO;
  }
  setTabIndex(next);
}, [location.pathname]);

const outletContext: ProductionFashionOutletType = {
  tabIndex,
  setTabIndex,
  pcpView,
  setPCPView,
  quantity,
  setQuantity,
  refresh,
  setRefresh,
  appliedFilters: activeFiltersState.appliedFilters,
  search,
};

if (startMatch || createMatch) {
  return (
    <ContentStartFashion>
      <Outlet context={outletContext} />
    </ContentStartFashion>
  );
}

if (boardMatch) {
  return (
    <>
      <ModuleKanban
        moduleType="production"
        moduleName="Produção"
        subModule="fashion"
        headerLeftContent={
          <>
            <SectionName>Produção</SectionName>
            <TabsDiv>
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  $isFirst={index === 0}
                  $isLast={index === tabs.length - 1}
                  $isActive={index === tabIndex}
                  onClick={() => {
                    if (index !== tabIndex) {
                      navigate(tab.path);
                    }
                  }}
                >
                  {index === TAB_INDEX_BOARD ? (
                    <LucideFilter size={12.8} style={{ marginRight: 7 }} />
                  ) : (
                    <img src={tab.image} alt={`${tab.path}-icon`} />
                  )}
                  {tab.name}
                </Tab>
              ))}
            </TabsDiv>
          </>
        }
      />
    </>
  );
}

return (
  <>
    <PageHeader
      title="Produção"
      tableName="Services"
      moduleName="Produção"
      tabs={tabs.map((tab) => ({ key: String(tab.index), label: tab.name, icon: tab.image }))}
      activeTab={String(tabIndex)}
      onTabChange={(key) => {
        const target = tabs.find((tab) => String(tab.index) === key);
        if (target) navigate(target.path);
      }}
      onReload={() => {
        setRefresh((r) => !r);
        eventBus.emit('refreshProductionTable');
      }}
      filterHeaders={
        isFilterableTab
          ? tabIndex === TAB_INDEX_MOVEMENT
            ? productionFashionMovementHeaders
            : productionFashionOrderHeaders
          : undefined
      }
      appliedFilters={isFilterableTab ? activeFiltersState.appliedFilters : undefined}
      onApplyFilter={isFilterableTab ? activeFiltersState.handleApplyFilter : undefined}
      onRemoveFilter={isFilterableTab ? activeFiltersState.handleRemoveFilter : undefined}
      onClearAllFilters={isFilterableTab ? activeFiltersState.handleClearAll : undefined}
      rightExtra={
        tabIndex === TAB_INDEX_PCP ? (
          <ViewModeSelector
            value={pcpView}
            onChange={(mode) => setPCPView(mode as PCPViewType)}
            boardLabel="Quadro"
            tableLabel="Tabela"
          />
        ) : undefined
      }
      createButton={
        isFilterableTab ? { label: 'Adicionar OP', onClick: handleAddOPClick } : undefined
      }
    />
    <Content>
      <Outlet context={outletContext} />
    </Content>
  </>
);
};

export default ProductionFashionContent;
